/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

interface NotificationsSliceState {
  homeBadgeCount?: number;
  messagesBadgeCount?: number;
  profileBadgeCount?: number;
  resultsBadgeCount?: number;
  scheduleBadgeCount?: number;
}

const initialState: NotificationsSliceState = {
  homeBadgeCount: undefined,
  messagesBadgeCount: undefined,
  profileBadgeCount: undefined,
  resultsBadgeCount: undefined,
  scheduleBadgeCount: undefined,
};

const notificationsSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "notifications",
  reducers: {
    setHomeBadgeCount: (state, action: PayloadAction<number>) => {
      state.homeBadgeCount = action.payload || undefined;
    },
    setMessagesBadgeCount: (state, action: PayloadAction<number>) => {
      state.messagesBadgeCount = action.payload || undefined;
    },
    setProfileBadgeCount: (state, action: PayloadAction<number>) => {
      state.profileBadgeCount = action.payload || undefined;
    },
    setResultsBadgeCount: (state, action: PayloadAction<number>) => {
      state.resultsBadgeCount = action.payload || undefined;
    },
    setScheduleBadgeCount: (state, action: PayloadAction<number>) => {
      state.scheduleBadgeCount = action.payload || undefined;
    },
  },
});

const {
  setHomeBadgeCount: setHomeBadgeCountAction,
  setMessagesBadgeCount: setMessagesBadgeCountAction,
  setProfileBadgeCount: setProfileBadgeCountAction,
  setResultsBadgeCount: setResultsBadgeCountAction,
  setScheduleBadgeCount: setScheduleBadgeCountAction,
} = notificationsSlice.actions;

const selectNotifications = (state: RootState) => state.notifications;

const useNotificationsDispatch = () => {
  const dispatch = useAppDispatch();

  const setHomeBadgeCount = (count: number) => {
    dispatch(setHomeBadgeCountAction(count));
  };

  const setMessagesBadgeCount = (count: number) => {
    dispatch(setMessagesBadgeCountAction(count));
  };

  const setProfileBadgeCount = (count: number) => {
    dispatch(setProfileBadgeCountAction(count));
  };

  const setResultsBadgeCount = (count: number) => {
    dispatch(setResultsBadgeCountAction(count));
  };

  const setScheduleBadgeCount = (count: number) => {
    dispatch(setScheduleBadgeCountAction(count));
  };

  return {
    setHomeBadgeCount,
    setMessagesBadgeCount,
    setProfileBadgeCount,
    setResultsBadgeCount,
    setScheduleBadgeCount,
  };
};

export type { NotificationsSliceState };
export {
  selectNotifications,
  setMessagesBadgeCountAction,
  useNotificationsDispatch,
};
export default notificationsSlice.reducer;
